import React, { useState, useEffect } from 'react';
import {
    LineChart,
    Line,
    XAxis,
    YAxis,
    Tooltip,
    ResponsiveContainer,
    CartesianGrid
} from 'recharts';
import {
    ChevronDown,
    ChevronUp,
    Clock,
    Search,
    RefreshCw,
    CheckCircle2,
    Clock3,
    Gauge
} from 'lucide-react';
import { supabase } from './supabase';

const GRAPH_URL = 'https://graph.pulsechain.com/subgraphs/name/pulsechain/bridge';
const GAS_API_URL = 'https://data.pulsex.win/gasnow.json';

// Gas Tracker Component
const GasTracker = () => {
    const [gasData, setGasData] = useState({
        slow: 0,
        standard: 0,
        fast: 0,
        rapid: 0,
        timestamp: 0
    });
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // Format gas price with K, M, B suffixes and 2 decimal places
    const formatGasPrice = (price) => {
        if (price >= 1000000000) {
            return (price / 1000000000).toFixed(2) + 'B';
        } else if (price >= 1000000) {
            return (price / 1000000).toFixed(2) + 'M';
        } else if (price >= 1000) {
            return (price / 1000).toFixed(2) + 'K';
        } else {
            return price.toFixed(2);
        }
    };

    // Fetch gas data
    const fetchGasData = async () => {
        try {
            const response = await fetch(GAS_API_URL);
            if (!response.ok) {
                throw new Error(`Network response was not ok: ${response.status}`);
            }
            const data = await response.json();
            setGasData(data);
            setLoading(false);
        } catch (err) {
            setError(err.message);
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchGasData();
        // Refresh gas data every 30 seconds
        const interval = setInterval(fetchGasData, 30000);
        return () => clearInterval(interval);
    }, []);

    return (
        <div className="bg-gray-800/70 backdrop-blur-xl rounded-2xl shadow-sm border border-gray-700/50 p-4 mb-6">
            <div className="flex flex-col md:flex-row items-center justify-between">
                <div className="flex items-center gap-2 mb-2 md:mb-0">
                    <Gauge className="text-violet-400" size={20} />
                    <h2 className="text-lg font-medium text-gray-200">Gas Prices</h2>
                </div>
                {loading ? (
                    <div className="text-gray-400 text-sm">Loading gas data...</div>
                ) : error ? (
                    <div className="text-red-400 text-sm">Error: {error}</div>
                ) : (
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 w-full md:w-auto">
                        <div className="bg-gray-900/50 px-4 py-2 rounded-lg text-center">
                            <div className="text-xs text-gray-400">Slow</div>
                            <div className="text-emerald-400 font-medium">{formatGasPrice(gasData.slow)} beats</div>
                        </div>
                        <div className="bg-gray-900/50 px-4 py-2 rounded-lg text-center">
                            <div className="text-xs text-gray-400">Standard</div>
                            <div className="text-blue-400 font-medium">{formatGasPrice(gasData.standard)} beats</div>
                        </div>
                        <div className="bg-gray-900/50 px-4 py-2 rounded-lg text-center">
                            <div className="text-xs text-gray-400">Fast</div>
                            <div className="text-yellow-400 font-medium">{formatGasPrice(gasData.fast)} beats</div>
                        </div>
                        <div className="bg-gray-900/50 px-4 py-2 rounded-lg text-center">
                            <div className="text-xs text-gray-400">Rapid</div>
                            <div className="text-red-400 font-medium">{formatGasPrice(gasData.rapid)} beats</div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

const BridgeTracker = () => {
    const [trackedTxns, setTrackedTxns] = useState([]);
    const [completedTxns, setCompletedTxns] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [showAllPending, setShowAllPending] = useState(false);
    const [showAllCompleted, setShowAllCompleted] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [easterEggActivated, setEasterEggActivated] = useState(false);
    const [showEasterEggNotification, setShowEasterEggNotification] = useState(false);

    // Correctly format the timestamp *as a string*
    const formatTimestamp = (timestamp) => {
        return new Date(timestamp * 1000).toLocaleString();
    };
    
    // Easter egg handler for search term
    // Special function to reload directly from GraphQL for Easter egg
    const reloadDirectlyFromGraphQL = async () => {
        setLoading(true);
        try {
            // Fetch data directly from GraphQL
            const graphData = await fetchTransactions();
            
            if (!graphData) {
                setLoading(false);
                return;
            }
            
            // Process transactions directly from GraphQL
            const pendingTxns = [];
            const completedTxns = [];
            
            for (const tx of graphData.data.userRequests) {
                const signatures = tx.message?.signatures?.length || 0;
                const txHash = tx.txHash;
                
                // Create transaction object
                const txObj = {
                    tx_hash: txHash,
                    timestamp: tx.timestamp,
                    signatures: signatures,
                    message: tx.message
                };
                
                // Categorize based on signatures count
                if (signatures < 5) {
                    // Pending transaction
                    pendingTxns.push({
                        ...txObj,
                        completed: false
                    });
                } else {
                    // Completed transaction
                    completedTxns.push({
                        ...txObj,
                        completed: true,
                        completed_at: new Date().toISOString() // Approximate completion time
                    });
                }
            }
            
            // Update state with fresh data from GraphQL
            setTrackedTxns(pendingTxns);
            setCompletedTxns(completedTxns);
            
        } catch (error) {
            setError("Easter egg GQL fetch error: " + error.message);
        } finally {
            setLoading(false);
        }
    };

    const [searchResult, setSearchResult] = useState(null);
    const [searchError, setSearchError] = useState(null);
    const [isSearching, setIsSearching] = useState(false);

    const validateTxnHash = (hash) => {
        // Check if hash starts with "0x" and is followed by 64 hexadecimal characters
        const hexRegex = /^0x[0-9a-fA-F]{64}$/;
        return hexRegex.test(hash);
    };

    const searchTransactionByHash = async (txHash) => {
        if (!validateTxnHash(txHash)) {
            setSearchError("Invalid transaction hash format. Must start with '0x' followed by 64 hexadecimal characters.");
            return;
        }

        setIsSearching(true);
        setSearchError(null);
        
        try {
            // Search using GraphQL directly
            const query = `{
                userRequests(where: {txHash: "${txHash}"}) {
                    message {
                        signatures
                        txHash
                    }
                    txHash
                    timestamp
                }
            }`;

            const response = await fetch(GRAPH_URL, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ query }),
            });

            if (!response.ok) {
                throw new Error(`Network response was not ok: ${response.status}`);
            }

            const result = await response.json();
            
            if (result.data.userRequests.length === 0) {
                setSearchError("Transaction not found. Please check the hash and try again.");
                setSearchResult(null);
            } else {
                const tx = result.data.userRequests[0];
                const signatures = tx.message?.signatures?.length || 0;
                
                setSearchResult({
                    tx_hash: tx.txHash,
                    timestamp: tx.timestamp,
                    signatures: signatures,
                    message: tx.message,
                    completed: signatures >= 5,
                    formattedTime: formatTimestamp(tx.timestamp)
                });
            }
        } catch (error) {
            console.error("Search error:", error);
            setSearchError("Error searching for transaction: " + error.message);
            setSearchResult(null);
        } finally {
            setIsSearching(false);
        }
    };

    const handleSearchChange = (e) => {
        const value = e.target.value;
        setSearchTerm(value);
        
        // Clear search result when input changes
        if (searchResult) {
            setSearchResult(null);
        }
        
        // Easter egg: typing "poseidon" will clear cache and reload transactions
        if (value.toLowerCase() === "poseidon") {
            // Visual feedback
            setEasterEggActivated(true);
            
            // Clear browser cache for this page
            if ('caches' in window) {
                caches.keys().then((names) => {
                    names.forEach(name => {
                        caches.delete(name);
                    });
                });
            }
            
            // Show notification
            setShowEasterEggNotification(true);
            
            // Show visual feedback and perform actions
            setTimeout(() => {
                setSearchTerm('');
                // Reload transactions data directly from GraphQL instead of Supabase
                reloadDirectlyFromGraphQL();
                
                // Reset visual feedback after a delay
                setTimeout(() => {
                    setEasterEggActivated(false);
                    
                    // Hide notification after 3 seconds
                    setTimeout(() => {
                        setShowEasterEggNotification(false);
                    }, 3000);
                }, 1000);
            }, 500);
        }
    };
    
    const handleSearchSubmit = (e) => {
        e.preventDefault();
        if (searchTerm.trim()) {
            searchTransactionByHash(searchTerm.trim());
        }
    };
    
    // Function removed as we're using shortened txn hash instead of timestamp

    const getTimeDifference = (startTime, endTime) => {
        const diffInMinutes = (endTime - startTime) / 60;
        return diffInMinutes.toFixed(2);
    };

    const fetchTransactions = async () => {
        const query = `{
            userRequests(first: 100, orderBy: timestamp, orderDirection: desc) {
              message {
                signatures
                txHash
              }
              txHash
              timestamp
            }
          }`;

        try {
            const response = await fetch(GRAPH_URL, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ query }),
            });

            if (!response.ok) {
                throw new Error(`Network response was not ok: ${response.status}`);
            }

            return await response.json();
        } catch (err) {
            setError(err.message);
            return null;
        }
    };

     const fetchSupabaseData = async () => {
        try {
            const { data: pendingData, error: pendingError } = await supabase
                .from('user_requests')
                .select('*')
                .eq('completed', false)
                .order('timestamp', { ascending: false });

            if (pendingError) throw pendingError;
            setTrackedTxns(pendingData || []);

            const { data: completedData, error: completedError } = await supabase
                .from('user_requests')
                .select('*')
                .eq('completed', true)
                .order('completed_at', { ascending: false })
                .limit(100);

            if (completedError) throw completedError;
            setCompletedTxns(completedData || []);

        } catch (error) {
            setError(error.message);
        }
    };

    // Process transactions function moved outside useEffect to be used by Easter egg
    const processTransactions = async () => {
        setLoading(true);
        const graphData = await fetchTransactions();

        if (!graphData) {
            setLoading(false);
            return;
        }

        try {
            for (const tx of graphData.data.userRequests) {
                const signatures = tx.message?.signatures?.length || 0;
                const txHash = tx.txHash;

                if (signatures < 5) {
                    const { data: existingTx, error: existingTxError } = await supabase
                        .from('user_requests')
                        .select('*')
                        .eq('tx_hash', txHash)
                        .maybeSingle();

                    if (existingTxError) {
                        console.error("Error checking for existing transaction:", existingTxError);
                        continue;
                    }

                    if (existingTx) {
                        if (signatures !== existingTx.signatures) {
                            const { error: updateError } = await supabase
                                .from('user_requests')
                                .update({ signatures: signatures })
                                .eq('tx_hash', txHash);

                            if (updateError) {
                                console.error("Error updating signatures:", updateError);
                            }
                        }
                    } else {
                        const { error: insertError } = await supabase
                            .from('user_requests')
                            .insert([
                                {
                                    tx_hash: txHash,
                                    timestamp: tx.timestamp,
                                    signatures,
                                    message: tx.message,
                                    completed: false,
                                },
                            ]);

                        if (insertError) {
                            if (insertError.code === '23505') {
                                console.warn(`Transaction ${txHash} already exists (duplicate insert attempt). Skipping.`);
                            } else {
                                console.error("Error inserting transaction:", insertError);
                            }
                        }
                    }
                } else if (signatures >= 5) {
                    const { data: existingTx, error: existingTxError } = await supabase
                        .from('user_requests')
                        .select('*')
                        .eq('tx_hash', txHash)
                        .maybeSingle();

                     if (existingTxError) {
                        console.error("Error checking for existing transaction:", existingTxError);
                        continue;
                    }

                    if(existingTx){
                        if(!existingTx.completed){
                            const { error: updateError } = await supabase
                                .from('user_requests')
                                .update({ completed: true, signatures: signatures, completed_at: new Date().toISOString() })
                                .eq('tx_hash', txHash);

                            if (updateError) {
                                console.error("Error updating transaction (completed):", updateError);
                            }
                        }
                    }
                }
            }
        } catch (error) {
            setError(error.message);
        } finally {
              await fetchSupabaseData();
              setLoading(false);
        }
    };

    useEffect(() => {
        // On initial load, get fresh data directly from GraphQL
        reloadDirectlyFromGraphQL();
        
        // Then continue with regular process that updates Supabase
        processTransactions();
        const interval = setInterval(processTransactions, 60000);

        return () => clearInterval(interval);
    }, []);


    const lastClearedTime = completedTxns.length > 0 && completedTxns[0].completed_at
    ? getTimeDifference(parseInt(completedTxns[0].timestamp), Math.floor(new Date(completedTxns[0].completed_at).getTime() / 1000))
    : 'N/A';
    const queuedTxnsCount = trackedTxns.length;

    // Get the last 30 completed transactions for the chart
    const last30CompletedTxns = completedTxns.slice(0, 30);

    // Prepare chart data with just these 30 transactions
    const chartData = last30CompletedTxns
        .map((tx) => ({
            name: tx.tx_hash.substring(0, 6),  // Shortened transaction hash for x-axis
            timeTaken: tx.completed_at ? getTimeDifference(parseInt(tx.timestamp), Math.floor(new Date(tx.completed_at).getTime()/1000)) : 0,
            fullHash: tx.tx_hash,  // Store the full hash for tooltip
            date: formatTimestamp(tx.timestamp)  // Store the date for tooltip
        }));

    const filteredPending = trackedTxns
        .filter(tx => tx.tx_hash.toLowerCase().includes(searchTerm.toLowerCase()));


    return (
        <div className="min-h-screen bg-gray-900 text-white">
            {/* Easter Egg Notification */}
            {showEasterEggNotification && (
                <div className="fixed top-4 right-4 z-50 p-4 bg-emerald-500 text-white rounded-lg shadow-lg animate-bounce">
                    <div className="flex items-center gap-2">
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                        </svg>
                        <span>Fresh data loaded directly from GraphQL!</span>
                    </div>
                </div>
            )}
            
            <div className="max-w-7xl mx-auto space-y-6 p-8">
                <div className="bg-gray-800/70 backdrop-blur-xl rounded-2xl shadow-sm border border-gray-700/50 p-8">
                    <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
                        <div>
                            <h1 className="text-3xl font-bold bg-gradient-to-r from-violet-400 to-indigo-400 bg-clip-text text-transparent mb-2">PulseChain Bridge Status</h1>
                            <p className="text-gray-400">Real-time monitoring of bridge transactions</p>
                            <a
                                href="https://x.com/Poseidon_5555"
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-sm text-blue-400 hover:text-blue-300 transition-colors"
                            >
                                Made by Poseidon
                            </a>
                        </div>
                        <div className="flex items-center gap-6 bg-gray-800/50 backdrop-blur-xl px-6 py-3 rounded-2xl border border-gray-700/50">
                            <div className="flex items-center gap-2">
                                <Clock3 className="w-5 h-5 text-violet-400" />
                                <div>
                                    <p className="text-sm text-gray-400">Last Clear</p>
                                    <p className="text-lg font-semibold text-gray-200">{lastClearedTime} min</p>
                                </div>
                            </div>
                            <div className="w-px h-10 bg-gray-700/50" />
                            <div className="flex items-center gap-2">
                                <RefreshCw className="w-5 h-5 text-violet-400" />
                                <div>
                                    <p className="text-sm text-gray-400">Queued</p>
                                    <p className="text-lg font-semibold text-gray-200">{queuedTxnsCount}</p>
                                </div>
                            </div>
                            <div className="w-px h-10 bg-gray-700/50" />
                            <button 
                                onClick={() => {
                                    setEasterEggActivated(true);
                                    setShowEasterEggNotification(true);
                                    reloadDirectlyFromGraphQL();
                                    
                                    setTimeout(() => {
                                        setEasterEggActivated(false);
                                        
                                        setTimeout(() => {
                                            setShowEasterEggNotification(false);
                                        }, 3000);
                                    }, 1000);
                                }}
                                className="flex items-center gap-2 text-violet-400 hover:text-violet-300 transition-colors"
                            >
                                <RefreshCw className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} />
                                <span className="text-sm">Refresh</span>
                            </button>
                        </div>
                    </div>
                </div>
                
                {/* Gas Tracker */}
                <GasTracker />

               <div className="bg-gray-800/70 backdrop-blur-xl rounded-2xl shadow-sm border border-gray-700/50 p-6">
                    <h2 className="text-xl font-semibold text-gray-200 mb-4">Bridge Processing Time (Last 30 Transactions)</h2>
                    <div className="h-80">
                        <ResponsiveContainer width="100%" height="100%">
                            <LineChart data={chartData} margin={{ top: 5, right: 30, left: 20, bottom: 20 }}>
                                <CartesianGrid strokeDasharray="3 3" stroke="#444" />
                                <XAxis 
                                    dataKey="name" 
                                    stroke="#bbb"
                                    angle={0}
                                    textAnchor="middle"
                                    height={50}
                                    tick={{ fontSize: 11, fontFamily: 'monospace' }}
                                    interval={2} // Show every third tick for 30 transactions
                                />
                                <YAxis stroke="#bbb" />
                                <Tooltip
                                    contentStyle={{
                                        backgroundColor: 'rgba(31, 41, 55, 0.8)',
                                        backdropFilter: 'blur(8px)',
                                        border: '1px solid rgba(63, 78, 96, 0.5)',
                                        borderRadius: '1rem',
                                        boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)',
                                        color: '#fff'
                                    }}
                                    formatter={(value, name) => [`${value} minutes`, 'Time Taken']}
                                    labelFormatter={(label, payload) => {
                                        if (payload && payload.length > 0) {
                                            return `${payload[0].payload.fullHash}\n${payload[0].payload.date}`;
                                        }
                                        return label;
                                    }}
                                />
                                <Line
                                    type="monotone"
                                    dataKey="timeTaken"
                                    stroke="#a855f7"
                                    strokeWidth={2}
                                    dot={false} // Don't show dots for all points
                                    activeDot={{ r: 6, fill: '#9333ea' }}
                                />
                            </LineChart>
                        </ResponsiveContainer>
                    </div>
                </div>

                <form onSubmit={handleSearchSubmit} className="relative">
                    <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-500 w-5 h-5" />
                    <input
                        type="text"
                        placeholder="Search by transaction hash (0x...)"
                        className={`w-full pl-12 pr-24 py-3 border ${easterEggActivated ? 'border-emerald-400' : 'border-gray-700/50'} rounded-2xl focus:ring-2 ${easterEggActivated ? 'ring-emerald-400' : 'focus:ring-violet-400'} focus:border-violet-400 transition-all bg-gray-800/70 backdrop-blur-xl shadow-sm text-gray-200 ${easterEggActivated ? 'animate-pulse' : ''}`}
                        value={searchTerm}
                        onChange={handleSearchChange}
                    />
                    <button
                        type="submit"
                        className="absolute right-2 top-1/2 transform -translate-y-1/2 px-4 py-1.5 bg-violet-500 hover:bg-violet-600 text-white rounded-xl transition-colors"
                        disabled={isSearching}
                    >
                        {isSearching ? "Searching..." : "Search"}
                    </button>
                </form>
                
                {/* Search Error Message */}
                {searchError && (
                    <div className="mt-2 p-3 bg-red-500/20 border border-red-500/50 rounded-xl text-red-300">
                        {searchError}
                    </div>
                )}
                
                {/* Transaction Search Result Popup */}
                {searchResult && (
                    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50">
                        <div className="bg-gray-800 border border-gray-700 rounded-2xl shadow-lg max-w-2xl w-full mx-4 overflow-hidden">
                            <div className="p-6 border-b border-gray-700">
                                <div className="flex justify-between items-center">
                                    <h3 className="text-xl font-bold text-gray-200">Transaction Details</h3>
                                    <button 
                                        onClick={() => setSearchResult(null)}
                                        className="text-gray-400 hover:text-white"
                                    >
                                        <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                        </svg>
                                    </button>
                                </div>
                            </div>
                            <div className="p-6 space-y-4">
                                <div>
                                    <h4 className="text-sm text-gray-400 mb-1">Transaction Hash</h4>
                                    <div className="bg-gray-900 p-3 rounded-lg break-all font-mono text-violet-400">
                                        <a 
                                            href={`https://scan.pulsechain.com/tx/${searchResult.tx_hash}`} 
                                            target="_blank" 
                                            rel="noopener noreferrer"
                                            className="hover:text-violet-300 transition-colors"
                                        >
                                            {searchResult.tx_hash}
                                        </a>
                                    </div>
                                </div>
                                
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div>
                                        <h4 className="text-sm text-gray-400 mb-1">Timestamp</h4>
                                        <div className="bg-gray-900 p-3 rounded-lg">
                                            {searchResult.formattedTime}
                                        </div>
                                    </div>
                                    
                                    <div>
                                        <h4 className="text-sm text-gray-400 mb-1">Status</h4>
                                        <div className="bg-gray-900 p-3 rounded-lg">
                                            {searchResult.completed ? (
                                                <span className="flex items-center text-emerald-400">
                                                    <CheckCircle2 className="w-4 h-4 mr-2" />
                                                    Completed
                                                </span>
                                            ) : (
                                                <span className="flex items-center text-amber-400">
                                                    <Clock3 className="w-4 h-4 mr-2" />
                                                    Pending ({searchResult.signatures}/5 signatures)
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                </div>
                                
                                <div>
                                    <h4 className="text-sm text-gray-400 mb-1">Signatures</h4>
                                    <div className="bg-gray-900 p-3 rounded-lg">
                                        <div className="flex justify-between mb-2">
                                            <span>Progress:</span>
                                            <span>{searchResult.signatures}/5</span>
                                        </div>
                                        <div className="w-full bg-gray-700 rounded-full h-2.5">
                                            <div 
                                                className={`h-2.5 rounded-full ${searchResult.completed ? 'bg-emerald-500' : 'bg-violet-500'}`} 
                                                style={{width: `${(searchResult.signatures / 5) * 100}%`}}
                                            ></div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div className="bg-gray-900/50 p-4 flex justify-end">
                                <button
                                    onClick={() => setSearchResult(null)}
                                    className="px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-colors"
                                >
                                    Close
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    <div className="bg-gray-800/70 backdrop-blur-xl rounded-2xl shadow-sm border border-gray-700/50 overflow-hidden">
                        <div className="p-6 border-b border-gray-700/50">
                            <div className="flex justify-between items-center">
                                <div className="flex items-center gap-2">
                                    <Clock className="w-5 h-5 text-violet-400" />
                                    <h2 className="text-xl font-semibold text-gray-200">
                                        Pending ({filteredPending.length})
                                    </h2>
                                </div>
                                <button
                                    onClick={() => setShowAllPending(!showAllPending)}
                                    className="text-gray-500 hover:text-violet-400 transition-colors"
                                >
                                    {showAllPending ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
                                </button>
                            </div>
                        </div>
                        <div className="overflow-x-auto">
                            <table className="w-full">
                                <thead>
                                    <tr className="bg-gray-700/50">
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Hash</th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Time</th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Status</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-700/50">
                                    {filteredPending
                                        .slice(0, showAllPending ? undefined : 5)
                                        .map((tx) => (
                                            <tr key={tx.tx_hash} className="hover:bg-gray-700/50 transition-colors">
                                                <td className="px-6 py-4">
                                                    <a href={`https://scan.pulsechain.com/tx/${tx.tx_hash}`} target="_blank" rel="noopener noreferrer" className="font-mono text-sm text-violet-400 hover:text-violet-300 transition-colors">{tx.tx_hash.substring(0, 10)}...</a>
                                                </td>
                                                <td className="px-6 py-4 text-sm text-gray-400">
                                                    {formatTimestamp(tx.timestamp)}
                                                </td>
                                                <td className="px-6 py-4">
                                                    <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-violet-700/20 text-violet-300">
                                                        {tx.signatures} / 5 signatures
                                                    </span>
                                                </td>
                                            </tr>
                                        ))}
                                </tbody>
                            </table>
                            {filteredPending.length === 0 && !loading && (
                                <div className="px-6 py-8 text-center text-gray-500">
                                    No pending transactions
                                </div>
                            )}
                        </div>
                    </div>

                    <div className="bg-gray-800/70 backdrop-blur-xl rounded-2xl shadow-sm border border-gray-700/50 overflow-hidden">
                        <div className="p-6 border-b border-gray-700/50">
                            <div className="flex justify-between items-center">
                                <div className="flex items-center gap-2">
                                    <CheckCircle2 className="w-5 h-5 text-emerald-500" />
                                    <h2 className="text-xl font-semibold text-gray-200">
                                        Completed ({completedTxns.length})
                                    </h2>
                                </div>
                                <button
                                    onClick={() => setShowAllCompleted(!showAllCompleted)}
                                    className="text-gray-500 hover:text-violet-400 transition-colors"
                                >
                                    {showAllCompleted ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
                                </button>
                            </div>
                        </div>
                        <div className="overflow-x-auto">
                            <table className="w-full">
                                <thead>
                                <tr className="bg-gray-700/50">
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Hash</th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Time</th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Duration</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-700/50">
                                    {(showAllCompleted ? completedTxns : completedTxns.slice(0, 5))
                                        .map((tx) => (
                                            <tr key={tx.tx_hash} className="hover:bg-gray-700/50 transition-colors">
                                                <td className="px-6 py-4">
                                                    <a href={`https://scan.pulsechain.com/tx/${tx.tx_hash}`} target="_blank" rel="noopener noreferrer" className="font-mono text-sm text-violet-400 hover:text-violet-300 transition-colors">{tx.tx_hash.substring(0, 10)}...</a>
                                                </td>
                                                <td className="px-6 py-4 text-sm text-gray-400">
                                                    {formatTimestamp(tx.timestamp)}
                                                </td>
                                                <td className="px-6 py-4 text-sm text-gray-400">
                                                    {tx.completed_at ? getTimeDifference(parseInt(tx.timestamp), Math.floor(new Date(tx.completed_at).getTime() / 1000)) : 'N/A'} min
                                                </td>
                                            </tr>
                                        ))}
                                </tbody>
                            </table>
                            {completedTxns.length === 0 && !loading && (
                                <div className="px-6 py-8 text-center text-gray-500">
                                    No completed transactions
                                </div>
                            )}
                        </div>
                    </div>
                </div>
                 {error && (
                    <div className="p-4 text-red-500 dark:text-red-400">
                        Error: {error}
                    </div>
                )}
                {loading && (
                    <div className="p-4 text-center text-gray-500">
                        Loading transactions...
                    </div>
                )}
            </div>
        </div>
    );
};

export default BridgeTracker;